import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

interface CampaignContextType {
  selectedLocations: string[];
  selectedJobRole: string | null;
  setCampaign: (locations: string[], role: string) => void;
  clearCampaign: () => void;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const STORAGE_KEY_LOCATIONS = 'campaign_locations';
const STORAGE_KEY_ROLE = 'campaign_role';

interface CampaignProviderProps {
  children: ReactNode;
}

export function CampaignProvider({ children }: CampaignProviderProps) {
  // Initialize state from localStorage or defaults
  const [selectedLocations, setSelectedLocations] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_LOCATIONS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load locations from localStorage:', error);
      return [];
    }
  });

  const [selectedJobRole, setSelectedJobRole] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_ROLE);
    } catch (error) {
      console.error('Failed to load job role from localStorage:', error);
      return null;
    }
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      if (selectedLocations.length > 0) {
        localStorage.setItem(STORAGE_KEY_LOCATIONS, JSON.stringify(selectedLocations));
      } else {
        localStorage.removeItem(STORAGE_KEY_LOCATIONS);
      }
    } catch (error) {
      console.error('Failed to save locations to localStorage:', error);
    }
  }, [selectedLocations]);

  useEffect(() => {
    try {
      if (selectedJobRole) {
        localStorage.setItem(STORAGE_KEY_ROLE, selectedJobRole);
      } else {
        localStorage.removeItem(STORAGE_KEY_ROLE);
      }
    } catch (error) {
      console.error('Failed to save job role to localStorage:', error);
    }
  }, [selectedJobRole]);

  // Memoize callbacks to prevent re-creating on every render
  const setCampaign = useCallback((locations: string[], role: string) => {
    setSelectedLocations(locations);
    setSelectedJobRole(role);
  }, []);

  const clearCampaign = useCallback(() => {
    setSelectedLocations([]);
    setSelectedJobRole(null);
  }, []);

  // Memoize the context value to prevent re-renders when nothing has changed
  const value: CampaignContextType = useMemo(() => ({
    selectedLocations,
    selectedJobRole,
    setCampaign,
    clearCampaign,
  }), [selectedLocations, selectedJobRole, setCampaign, clearCampaign]);

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
}

// Custom hook for easy access to campaign context
export function useCampaign(): CampaignContextType {
  const context = useContext(CampaignContext);
  
  if (context === undefined) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  
  return context;
}


